// src/pages/BlogPost.jsx
// Route: /blog/:slug
// Renders a single blog post with full SEO

import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, Tag, ChevronRight, ArrowLeft, TrendingUp } from 'lucide-react';
import { getPostBySlug, getRecentPosts, formatDate } from '../data/blogPosts';

function setMeta(name, content, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

// ── Markdown-like renderer ───────────────────────────────────────────────────
// Converts the plain text content format into styled HTML
// Supports: ## H2, ### H3, **bold**, paragraph breaks, *italic*
function renderContent(content) {
  if (!content) return [];

  return content.split('\n\n').map((block, i) => {
    const key = i;

    if (block.startsWith('## ')) {
      return (
        <h2 key={key} className="text-2xl font-bold text-gray-800 mt-8 mb-3 pb-2 border-b border-gray-200">
          {block.slice(3)}
        </h2>
      );
    }
    if (block.startsWith('### ')) {
      return (
        <h3 key={key} className="text-xl font-bold text-gray-800 mt-6 mb-2">
          {block.slice(4)}
        </h3>
      );
    }
    if (block.startsWith('- ')) {
      const items = block.split('\n').filter(l => l.startsWith('- ')).map(l => l.slice(2));
      return (
        <ul key={key} className="list-none space-y-2 my-4">
          {items.map((item, j) => (
            <li key={j} className="flex items-start gap-2 text-gray-700">
              <span className="text-yellow-500 font-bold mt-0.5">•</span>
              <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>') }} />
            </li>
          ))}
        </ul>
      );
    }
    // Italic disclaimer
    if (block.startsWith('*') && block.endsWith('*')) {
      return (
        <p key={key} className="text-sm text-gray-400 italic border-l-4 border-gray-200 pl-4 my-6">
          {block.slice(1, -1)}
        </p>
      );
    }
    // Regular paragraph — apply bold and italic inline
    const html = block
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>');
    return (
      <p key={key} className="text-gray-700 leading-relaxed my-4"
         dangerouslySetInnerHTML={{ __html: html }} />
    );
  });
}

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Link to="/" className="text-2xl font-bold text-yellow-600 hover:text-yellow-700 transition-colors">
            LivePriceForGold.com
          </Link>
          <div className="flex items-center gap-8">
            <Link to="/"               className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Home</Link>
            <a href="/calculator.html" className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Gold Calculator</a>
            <Link to="/blog"           className="text-sm font-medium text-yellow-600 font-semibold">Blog</Link>
            <a href="/about.html"      className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">About Us</a>
            <a href="/contact.html"    className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Contact</a>
          </div>
        </div>
      </div>
    </nav>
  );
}

// ── 404 ───────────────────────────────────────────────────────────────────────
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-yellow-50">
      <div className="text-center p-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">Article Not Found</h1>
        <p className="text-gray-600 mb-6">This article does not exist or has been removed.</p>
        <Link to="/blog" className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors font-semibold">
          Back to Blog
        </Link>
      </div>
    </div>
  );
}

// ── Main BlogPost ─────────────────────────────────────────────────────────────
export default function BlogPost() {
  const { slug } = useParams();
  const post     = getPostBySlug(slug);
  const related  = getRecentPosts(3).filter(p => p.slug !== slug).slice(0, 2);

  useEffect(() => {
    if (!post) return;

    const canonical = `https://livepriceforgold.com/blog/${post.slug}`;
    document.title = `${post.title} | LivePriceForGold.com`;
    setMeta('description', post.excerpt);
    setMeta('keywords', post.tags.join(', '));
    setMeta('og:title',       post.title, 'property');
    setMeta('og:description', post.excerpt, 'property');
    setMeta('og:url',         canonical, 'property');
    setMeta('og:type',        'article', 'property');
    setMeta('article:published_time', post.date, 'property');
    setMeta('article:author', post.author, 'property');

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.rel = 'canonical'; document.head.appendChild(link); }
    link.href = canonical;

    // JSON-LD Article schema
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      dateModified: post.date,
      author: { '@type': 'Organization', name: post.author },
      publisher: {
        '@type': 'Organization',
        name: 'LivePriceForGold.com',
        url: 'https://livepriceforgold.com',
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
      keywords: post.tags.join(', '),
      articleSection: post.category,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home',       item: 'https://livepriceforgold.com' },
          { '@type': 'ListItem', position: 2, name: 'Blog',       item: 'https://livepriceforgold.com/blog' },
          { '@type': 'ListItem', position: 3, name: post.title,   item: canonical },
        ],
      },
    };

    let ld = document.getElementById('ld-json-blog');
    if (!ld) { ld = document.createElement('script'); ld.id = 'ld-json-blog'; ld.type = 'application/ld+json'; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify(schema);

    return () => { if (ld?.parentNode) ld.parentNode.removeChild(ld); };
  }, [post]);

  if (!post) return <NotFound />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-6 pt-6">
        <nav className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <Link to="/" className="hover:text-yellow-600">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:text-yellow-600">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-700 truncate max-w-xs">{post.title}</span>
        </nav>
      </div>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">

          {/* Article header */}
          <div className="bg-gradient-to-r from-yellow-600 to-amber-600 px-8 py-10 text-white">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="bg-white/25 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                {post.category}
              </span>
              <span className="flex items-center gap-1 text-yellow-100 text-xs">
                <Clock className="w-3 h-3" />{post.readTime} min read
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold leading-tight mb-4">{post.title}</h1>
            <div className="flex items-center gap-4 text-yellow-200 text-sm flex-wrap">
              <span>{post.author}</span>
              <span>·</span>
              <time dateTime={post.date}>{formatDate(post.date)}</time>
            </div>
          </div>

          {/* Article body */}
          <div className="px-8 py-8">
            {/* Excerpt / lead */}
            <p className="text-lg text-gray-600 leading-relaxed border-l-4 border-yellow-400 pl-5 mb-8 font-medium">
              {post.excerpt}
            </p>

            {/* Content */}
            <div className="prose-like">
              {renderContent(post.content)}
            </div>

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-gray-400" />
                {post.tags.map(tag => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 px-3 py-1 rounded-full hover:bg-yellow-100 transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* Back link */}
            <div className="mt-8">
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-yellow-600 font-semibold hover:gap-3 transition-all">
                <ArrowLeft className="w-4 h-4" />
                Back to all articles
              </Link>
            </div>
          </div>
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-gray-800 mb-5">More Articles</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {related.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`}
                  className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition-all group">
                  <span className="text-xs font-bold text-yellow-600 uppercase tracking-wider">{p.category}</span>
                  <h3 className="font-bold text-gray-800 mt-2 mb-2 group-hover:text-yellow-600 transition-colors line-clamp-2">
                    {p.title}
                  </h3>
                  <p className="text-gray-500 text-xs line-clamp-2">{p.excerpt}</p>
                  <p className="text-gray-400 text-xs mt-3">{formatDate(p.date)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 bg-gradient-to-r from-yellow-500 to-amber-600 rounded-2xl p-8 text-white text-center">
          <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-80" />
          <h3 className="text-xl font-bold mb-2">Check Live Gold Prices</h3>
          <p className="text-yellow-100 text-sm mb-4">Real-time gold rates in 140+ currencies, updated every 5 minutes.</p>
          <Link to="/" className="inline-block bg-white text-yellow-600 font-bold px-6 py-3 rounded-xl hover:bg-yellow-50 transition-colors">
            View Live Gold Rates →
          </Link>
        </div>
      </article>

      {/* Footer */}
      <div className="bg-gray-800 text-white py-8 px-6 mt-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs opacity-60 mb-2">Articles are for informational purposes only and do not constitute financial advice.</p>
          <p className="text-xs opacity-60">© {new Date().getFullYear()} LivePriceForGold.com. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
